import { createContext, useContext, useState, useEffect } from 'react'
import { authApi } from '../services/authApi'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [operator, setOperator] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for stored token on mount
    const token = localStorage.getItem('operator_token')
    if (token) {
      // Verify token is still valid
      verifyToken(token)
    } else {
      setLoading(false)
    }
  }, [])

  async function verifyToken(token) {
    try {
      const operator = await authApi.getMe()
      setOperator(operator)
    } catch (error) {
      // Token invalid, remove it
      console.error('Token verification failed:', error)
      localStorage.removeItem('operator_token')
      setOperator(null)
    } finally {
      setLoading(false)
    }
  }

  async function login(email, password) {
    try {
      const result = await authApi.login(email, password)
      
      // Handle success response
      if (result && result.token) {
        localStorage.setItem('operator_token', result.token)
        setOperator(result.operator || result)
        return { success: true }
      }
      
      // Handle response format variations
      if (result && result.data) {
        const token = result.data.token
        const operator = result.data.operator || result.data
        
        if (token) {
          localStorage.setItem('operator_token', token)
          setOperator(operator)
          return { success: true }
        }
      }
      
      throw new Error('Invalid response from server')
    } catch (error) {
      // Error toast is handled in authApi
      return { success: false, error: error.message }
    }
  }

  async function logout() {
    try {
      await authApi.logout()
    } catch (error) {
      // Local session must clear even if the API logout call fails.
      console.error('Logout API failed:', error)
    } finally {
      localStorage.removeItem('operator_token')
      setOperator(null)
    }
  }

  function getAuthHeaders() {
    const token = localStorage.getItem('operator_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  const value = {
    operator,
    loading,
    login,
    logout,
    isAuthenticated: !!operator,
    getAuthHeaders,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
